/**
 * export-pl-excel.ts
 *
 * Exports the Project P&L Report portfolio data to a formatted Excel (.xlsx) file.
 *
 * - Exports all projects across all pages (not just the current page)
 * - Raw numeric values written as numbers so Excel can calculate on them
 * - File named "Project_PL_Report_YYYY-MM-DD.xlsx"
 *
 * Dependencies:
 *  - exceljs (already in package.json)
 */

import ExcelJS from 'exceljs';
import type { Project } from '@/types';
import { calculateGP, calculateBudgetGP } from '@/lib/gp-calculation';
import type { GpCalculationMethod } from '@/lib/gp-calculation';
import { getFinanceSettings } from '@/lib/finance-settings-storage';
import { fetchProjects } from '@/lib/data-service';
import type { ProjectFilterParams } from '@/lib/data-service';
import type { PaginationMetadata } from '@/types/pagination';

// ── Brand colours ────────────────────────────────────────────────────────────

const HEADER_BG  = '1F3864'; // Dark navy
const HEADER_TEXT = 'FFFFFF'; // White
const ALT_ROW_BG = 'F5F7FA'; // Light grey alternating rows

// ── Helper: effective GP method (matches page logic) ─────────────────────────

function canApplyMethod(method: GpCalculationMethod, project: Project): boolean {
  if (method === 'simple') return true;
  if (method === 'contribution') {
    return (
      project.actualLabourCosts !== undefined &&
      project.actualSubcontractorCosts !== undefined
    );
  }
  if (method === 'fully-loaded') {
    return project.actualOverheadAbsorption !== undefined;
  }
  return true;
}

function effectiveMethod(method: GpCalculationMethod, project: Project): GpCalculationMethod {
  return canApplyMethod(method, project) ? method : 'simple';
}

// ── Column definitions ───────────────────────────────────────────────────────

interface ColDef {
  header: string;
  key: string;
  width: number;
  numFmt?: string;
  getValue: (row: PLExportRow) => string | number | null;
}

interface PLExportRow {
  project: Project;
  actualMarginPct: number;
  budgetMarginPct: number;
  marginVariancePp: number;
  actualGP: number;
  budgetGP: number;
  actualRevenue: number;
  budgetRevenue: number;
  revenueVariance: number;
  actualCosts: number;
  budgetCosts: number;
  costVariance: number;
  actualHours: number;
  budgetHours: number;
  hoursVariance: number;
}

const COLUMNS: ColDef[] = [
  {
    header: 'Project Code',
    key: 'code',
    width: 16,
    getValue: (r) => r.project.code ?? '',
  },
  {
    header: 'Project Name',
    key: 'name',
    width: 32,
    getValue: (r) => r.project.name,
  },
  {
    header: 'Client',
    key: 'client',
    width: 24,
    getValue: (r) => r.project.clientNames ?? '',
  },
  {
    header: 'Budget Revenue (£)',
    key: 'budgetRevenue',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.budgetRevenue,
  },
  {
    header: 'Actual Revenue (£)',
    key: 'actualRevenue',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.actualRevenue,
  },
  {
    header: 'Revenue Variance (£)',
    key: 'revenueVariance',
    width: 20,
    numFmt: '£#,##0.00',
    getValue: (r) => r.revenueVariance,
  },
  {
    header: 'Budget Costs (£)',
    key: 'budgetCosts',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.budgetCosts,
  },
  {
    header: 'Actual Costs (£)',
    key: 'actualCosts',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.actualCosts,
  },
  {
    header: 'Cost Variance (£)',
    key: 'costVariance',
    width: 18,
    numFmt: '£#,##0.00',
    getValue: (r) => r.costVariance,
  },
  {
    header: 'Budget GP (£)',
    key: 'budgetGP',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.budgetGP,
  },
  {
    header: 'Actual GP (£)',
    key: 'actualGP',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.actualGP,
  },
  {
    header: 'Budget Margin (%)',
    key: 'budgetMarginPct',
    width: 16,
    numFmt: '0.0"%"',
    getValue: (r) => r.budgetMarginPct,
  },
  {
    header: 'Actual Margin (%)',
    key: 'actualMarginPct',
    width: 16,
    numFmt: '0.0"%"',
    getValue: (r) => r.actualMarginPct,
  },
  {
    header: 'Margin Variance (pp)',
    key: 'marginVariancePp',
    width: 18,
    numFmt: '0.0',
    getValue: (r) => r.marginVariancePp,
  },
  {
    header: 'Budget Hours',
    key: 'budgetHours',
    width: 14,
    numFmt: '0',
    getValue: (r) => r.budgetHours,
  },
  {
    header: 'Actual Hours',
    key: 'actualHours',
    width: 14,
    numFmt: '0',
    getValue: (r) => r.actualHours,
  },
  {
    header: 'Hours Variance',
    key: 'hoursVariance',
    width: 14,
    numFmt: '0',
    getValue: (r) => r.hoursVariance,
  },
  {
    header: 'Billable',
    key: 'billable',
    width: 12,
    getValue: (r) => (r.project.billable ?? true) ? 'Yes' : 'No',
  },
];

// Numeric column keys for totals row
const NUMERIC_KEYS = new Set([
  'budgetRevenue',
  'actualRevenue',
  'revenueVariance',
  'budgetCosts',
  'actualCosts',
  'costVariance',
  'budgetGP',
  'actualGP',
  'budgetHours',
  'actualHours',
  'hoursVariance',
]);

// ── Enrich project data ──────────────────────────────────────────────────────

function enrichProject(project: Project, gpMethod: GpCalculationMethod): PLExportRow {
  const method = effectiveMethod(gpMethod, project);

  const actualGPResult = calculateGP(
    {
      revenue: project.actualSales ?? 0,
      totalDirectCosts: project.actualCosts ?? 0,
      labourCosts: project.actualLabourCosts,
      subcontractorCosts: project.actualSubcontractorCosts,
      overheadAbsorption: project.actualOverheadAbsorption,
    },
    method,
  );

  const budgetRevenue = project.budgetRevenue ?? 0;
  const totalBudgetCosts = (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0);
  const budgetGPResult = calculateBudgetGP(
    { revenue: budgetRevenue, totalDirectCosts: totalBudgetCosts },
    'simple',
  );

  const actualRevenue = project.actualSales ?? 0;
  const actualCosts = project.actualCosts ?? 0;
  const budgetCosts = totalBudgetCosts;
  const actualHours = project.actualHours ?? 0;
  const budgetHours = project.budgetHours ?? 0;

  return {
    project,
    actualMarginPct: actualGPResult.grossMarginPct,
    budgetMarginPct: budgetGPResult.grossMarginPct,
    marginVariancePp: actualGPResult.grossMarginPct - budgetGPResult.grossMarginPct,
    actualGP: actualGPResult.grossProfit,
    budgetGP: budgetGPResult.grossProfit,
    actualRevenue,
    budgetRevenue,
    revenueVariance: actualRevenue - budgetRevenue,
    actualCosts,
    budgetCosts,
    costVariance: actualCosts - budgetCosts,
    actualHours,
    budgetHours,
    hoursVariance: actualHours - budgetHours,
  };
}

// ── Fetch all pages ──────────────────────────────────────────────────────────

const MAX_PAGES = 50;
const MAX_ROWS = 10000;

export async function fetchAllPLProjectPages(
  filters?: ProjectFilterParams,
): Promise<Project[]> {
  const allProjects: Project[] = [];
  const pageSize = 500;
  let page = 1;

  while (page <= MAX_PAGES && allProjects.length < MAX_ROWS) {
    const result = await fetchProjects('project-pl-report', { page, pageSize }, filters);

    let data: Project[];
    let pagination: PaginationMetadata | undefined;

    if (result && typeof result === 'object' && 'data' in result) {
      data = result.data as Project[];
      pagination = result.pagination as PaginationMetadata | undefined;
    } else if (Array.isArray(result)) {
      data = result as Project[];
    } else {
      break;
    }

    if (data.length === 0) break;
    allProjects.push(...data);

    if (!pagination?.hasNextPage) break;
    page++;
  }

  return allProjects;
}

// ── Main export function ─────────────────────────────────────────────────────

export async function exportPLToExcel(projects: Project[]): Promise<void> {
  const gpMethod = getFinanceSettings().gpCalculationMethod;
  const rows = projects.map((p) => enrichProject(p, gpMethod));

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Projects';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Project P&L', {
    views: [{ state: 'frozen', ySplit: 2 }],
  });

  // ── Title row ─────────────────────────────────────────────────────
  const titleRow = sheet.addRow(['Project P&L Report']);
  titleRow.height = 28;
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: HEADER_TEXT } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(1, 1, 1, COLUMNS.length);

  // ── Column header row ──────────────────────────────────────────────
  sheet.columns = COLUMNS.map((col) => ({
    key: col.key,
    width: col.width,
  }));

  const headerRow = sheet.addRow(COLUMNS.map((c) => c.header));
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
    };
  });

  // ── Data rows ──────────────────────────────────────────────────────
  rows.forEach((row, idx) => {
    const values = COLUMNS.map((col) => col.getValue(row));
    const dataRow = sheet.addRow(values);
    dataRow.height = 18;

    const isAlt = idx % 2 === 1;
    dataRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];

      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }

      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };

      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }

      // Colour-code negative margin variance
      if (colDef?.key === 'marginVariancePp' && typeof cell.value === 'number') {
        if (cell.value < -5) {
          cell.font = { size: 10, bold: true, color: { argb: 'DC2626' } };
        } else if (cell.value < -2) {
          cell.font = { size: 10, color: { argb: 'D97706' } };
        } else if (cell.value > 0) {
          cell.font = { size: 10, color: { argb: '16A34A' } };
        }
      }

      // Colour-code actual margin
      if (colDef?.key === 'actualMarginPct' && typeof cell.value === 'number') {
        if (cell.value < 0) {
          cell.font = { size: 10, bold: true, color: { argb: 'DC2626' } };
        } else if (cell.value < 10) {
          cell.font = { size: 10, color: { argb: 'D97706' } };
        }
      }
    });
  });

  // ── Totals row ─────────────────────────────────────────────────────
  if (rows.length > 0) {
    const totals = COLUMNS.map((col) => {
      if (NUMERIC_KEYS.has(col.key)) {
        return rows.reduce((sum, r) => {
          const v = col.getValue(r);
          return sum + (typeof v === 'number' ? v : 0);
        }, 0);
      }
      if (col.key === 'name') return `Total (${rows.length} projects)`;
      return null;
    });

    const totalsRow = sheet.addRow(totals);
    totalsRow.height = 20;
    totalsRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      cell.font = { bold: true, size: 10 };
      cell.border = {
        top: { style: 'thin', color: { argb: '9FB4D8' } },
      };
      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }

  // ── Auto-filter on header row ──────────────────────────────────────
  sheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: COLUMNS.length },
  };

  // ── Download ───────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const fileName = `Project_PL_Report_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
